import React, {
  useCallback, useContext, useEffect, useMemo, useState
} from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';

import formatISO from 'date-fns/formatISO';
import { modelsIndexPollInterval } from '../../../config';

import {
  ADD_MODEL_COMMENT_QUERY, ADD_PIPELINE_COMMENT_QUERY, DELETE_MODEL_COMMENT_QUERY, DELETE_PIPELINE_COMMENT_QUERY,
  FETCH_ALL_MODEL_COMMENTS_QUERY, FETCH_ALL_PIPELINE_COMMENTS_QUERY,
  FETCH_MODEL_COMMENTS_QUERY, FETCH_PIPELINES_COMMENTS_QUERY, UPDATE_MODEL_COMMENT_QUERY, UPDATE_PIPELINE_COMMENT_QUERY,
} from '../../../utils/model_server';
import useCommentsWithUsers from '../../../utils/hooks/use_comments_with_users';

import { useClientContext } from '../ClientData/ClientData';
import useUserContext from '../UserData/UserData';
import useWorkspaceContext from '../../workspaces/WorkspaceContext/WorkspaceContext';

const CommentDataContext = React.createContext({
  error: null,
  comments: [],
  loading: false,
  canLoadMoreComments: false,
  addComment: () => { },
  updateComment: () => { },
  deleteComment: () => { },
  loadMoreComments: () => { },
});

export const ReadOnlyModelCommentsProvider = ({ children }) => {
  const { isAuthEnabled } = useClientContext();
  const { currentWorkspaceId } = useWorkspaceContext();

  const COMMENT_LIMIT = 10;
  const commentsQueryResult = useQuery(FETCH_ALL_MODEL_COMMENTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: modelsIndexPollInterval,
    variables: { workspaceId: currentWorkspaceId, limit: COMMENT_LIMIT, idFilter: [] },
  });

  const comments
    = !commentsQueryResult.loading
      && !commentsQueryResult.error
      && Object.keys(commentsQueryResult.data).includes('model_variant_comment')
      ? commentsQueryResult.data.model_variant_comment.map(({ comment, model_pk_id, model }) => ({
        ...comment,
        model_id: model_pk_id,
        workspace_id: model?.model?.workspace_id,
      }))
      : null;

  const filteredComments = comments?.filter(({ workspace_id }) => workspace_id === currentWorkspaceId) || null;
  const { data: commentsWithUsers, error: userError, loading: usersLoading } = useCommentsWithUsers(filteredComments);

  const userErrorMessage = userError ? 'Could not load user data for comments' : null;
  const commentErrorMessage = commentsQueryResult?.error ? 'Could not retrieve any model comments.' : null

  const valueWhenAuthEnabledFalse = {
    error: 'Log in to see model comments.',
    loading: false,
    comments: [],
  }

  const commentDataContextValue = useMemo(() => ({
    comments: commentsWithUsers,
    error: commentErrorMessage || userErrorMessage,
    loading: commentsQueryResult?.loading || usersLoading
  }), [
    commentsWithUsers,
    commentErrorMessage,
    userErrorMessage,
    commentsQueryResult,
    usersLoading
  ])

  if (!isAuthEnabled) {
    return (
      <CommentDataContext.Provider
        value={valueWhenAuthEnabledFalse}
      >
        {children}
      </CommentDataContext.Provider>
    )
  }

  return (
    <CommentDataContext.Provider
      value={commentDataContextValue}
    >
      {children}
    </CommentDataContext.Provider>
  )
}

export const ModelCommentsProvider = ({ model, children }) => {
  const [canLoadMoreComments, setCanLoadMoreComments] = useState(true);

  const { isAuthEnabled } = useClientContext();
  const { userData } = useUserContext();
  const { currentWorkspaceId } = useWorkspaceContext();

  const COMMENT_LIMIT = 50;
  // We need to execute this query lazily because we need to wait for the model query to load
  // and give us it's primary ID.
  const [queryComments, commentsQueryResult] = useLazyQuery(FETCH_MODEL_COMMENTS_QUERY);
  const modelID = model?.id;
  useEffect(() => {
    if (!isAuthEnabled || !modelID) {
      return;
    }

    queryComments({ variables: { model_pk_id: modelID, limit: COMMENT_LIMIT, idFilter: [] } });
  }, [modelID, queryComments, isAuthEnabled]);

  const comments
    = commentsQueryResult.called
      && !commentsQueryResult.loading
      && !commentsQueryResult.error
      && Object.keys(commentsQueryResult.data).includes('model_variant_comment')
      ? commentsQueryResult.data.model_variant_comment.map(({ comment, model_pk_id, model }) => ({
        ...comment,
        model_id: model_pk_id,
        workspace_id: model?.model?.workspace_id,
      })) : null;

  const filteredComments = comments?.filter(({ workspace_id }) => workspace_id === currentWorkspaceId) || null;

  const handleLoadMoreComments = useCallback(() => {
    if (!filteredComments || !commentsQueryResult.fetchMore) {
      queryComments({ variables: { model_pk_id: modelID, limit: COMMENT_LIMIT, idFilter: [] } });
      return;
    }

    // The autoincrementing comment ID is used as the cursor for fetching more rows.
    const minCommentID = Math.min(...filteredComments.map(({ id }) => id));
    return commentsQueryResult
      .fetchMore({
        variables: {
          model_pk_id: modelID,
          limit: COMMENT_LIMIT,
          idFilter: [{ comment_id: { _lt: minCommentID } }],
        },
      })
      .then((response) => {
        const numComments = response.data?.model_variant_comment?.length;
        if (numComments === undefined) {
          return;
        }
        if (numComments < COMMENT_LIMIT) {
          setCanLoadMoreComments(false);
        }
      });
  }, [commentsQueryResult, filteredComments, modelID, queryComments]);

  const { data: commentsWithUsers, error: userError, loading: usersLoading } = useCommentsWithUsers(filteredComments);

  const [addModelComment] = useMutation(ADD_MODEL_COMMENT_QUERY, {
    update: (cache, { data: { insert_model_variant_comment_one } }) => {
      cache.writeQuery({
        query: FETCH_MODEL_COMMENTS_QUERY,
        variables: { model_pk_id: modelID },
        data: { model_variant_comment: [insert_model_variant_comment_one] },
      });
    },
  });

  const currentUserId = userData?.id;
  const handleAddComment = useCallback((message) => {
    if (!currentUserId || !modelID || !message) {
      return;
    }

    const tempCommentID = Math.random().toString();
    addModelComment({
      variables: { model_pk_id: modelID, message, user_id: currentUserId },
      optimisticResponse: {
        insert_model_variant_comment_one: {
          id: `temp-model-variant-comment-id__${tempCommentID}`,
          model_pk_id: modelID,
          __typename: 'model_variant_comment',
          comment_id: `temp-comment-id__${tempCommentID}`,
          comment: {
            id: `temp-comment-id__${tempCommentID}`,
            message,
            user_id: currentUserId,
            created_at: formatISO(new Date()),
            updated_at: formatISO(new Date()),
            deleted_at: null,
            __typename: 'comment',
          },
        },
      },
    });
  }, [addModelComment, currentUserId, modelID])

  const [updateModelComment] = useMutation(UPDATE_MODEL_COMMENT_QUERY, {});
  const handleUpdateComment = useCallback((commentID, updatedMessage) => {
    updateModelComment({ variables: { commentID, updatedMessage } });
  }, [updateModelComment]);

  const [deleteModelComment] = useMutation(DELETE_MODEL_COMMENT_QUERY, {});
  const handleDeleteComment = useCallback((commentID) => {
    deleteModelComment({ variables: { commentID, deletedAt: new Date().toISOString() } });
  }, [deleteModelComment]);

  // There's no great way to get a "has next page" variable back from GraphQL, so just once
  // after the initial render we'll check if we have fewer results than we asked for and, if
  // so, hide the "load more comments" button.
  const [hasInitialCommentLoadCompleted, setHasInitialCommentLoadCompleted] = useState(false);
  useEffect(() => {
    if (hasInitialCommentLoadCompleted || filteredComments === null) {
      return;
    }
    setHasInitialCommentLoadCompleted(true);

    if (filteredComments.length < COMMENT_LIMIT) {
      setCanLoadMoreComments(false);
    }
  }, [filteredComments, hasInitialCommentLoadCompleted]);

  const userErrorMessage = userError ? 'Could not load user data for comments' : null;
  const commentErrorMessage = commentsQueryResult?.error ? 'Could not retrieve any model comments.' : null

  const valueWhenAuthEnabledFalse = {
    error: 'Log in to see model comments.',
    loading: false,
    canLoadMoreComments: false,
    comments: [],
    addComment: () => { },
    updateComment: () => { },
    deleteComment: () => { },
    loadMoreComments: () => { },
  }

  const commentDataContextValue = useMemo(() => ({
    comments: commentsWithUsers,
    error: commentErrorMessage || userErrorMessage,
    loading: commentsQueryResult?.loading || usersLoading,
    canLoadMoreComments,
    addComment: handleAddComment,
    updateComment: handleUpdateComment,
    deleteComment: handleDeleteComment,
    loadMoreComments: handleLoadMoreComments,
  }), [
    commentsWithUsers,
    commentErrorMessage,
    userErrorMessage,
    commentsQueryResult,
    usersLoading,
    canLoadMoreComments,
    handleAddComment,
    handleUpdateComment,
    handleDeleteComment,
    handleLoadMoreComments,
  ])

  if (!isAuthEnabled) {
    return (
      <CommentDataContext.Provider
        value={valueWhenAuthEnabledFalse}
      >
        {children}
      </CommentDataContext.Provider>
    )
  }

  return (
    <CommentDataContext.Provider
      value={commentDataContextValue}
    >
      {children}
    </CommentDataContext.Provider>
  )
}

export const ReadOnlyPipelineCommentsProvider = ({ children }) => {
  const { isAuthEnabled } = useClientContext();
  const { currentWorkspaceId } = useWorkspaceContext();

  const COMMENT_LIMIT = 10;
  const commentsQueryResult = useQuery(FETCH_ALL_PIPELINE_COMMENTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: modelsIndexPollInterval,
    variables: { workspaceId: currentWorkspaceId, limit: COMMENT_LIMIT, idFilter: [] },
  });

  const comments
    = !commentsQueryResult.loading
      && !commentsQueryResult.error
      && Object.keys(commentsQueryResult.data).includes('pipeline_comment')
      ? commentsQueryResult.data.pipeline_comment.map(({ comment, pipeline_pk_id, pipeline }) => ({
        ...comment,
        pipeline_id: pipeline_pk_id,
        workspace_id: pipeline?.workspace_id,
      }))
      : null;

  const filteredComments = comments?.filter(({ workspace_id }) => workspace_id === currentWorkspaceId) || null;

  const { data: commentsWithUsers, error: userError, loading: usersLoading } = useCommentsWithUsers(filteredComments);

  const userErrorMessage = userError ? 'Could not load user data for comments' : null;
  const commentErrorMessage = commentsQueryResult?.error ? 'Could not retrieve pipeline comments.' : null

  const valueWhenAuthEnabledFalse = {
    error: 'Log in to see pipeline comments.',
    loading: false,
    comments: [],
  }

  const commentDataContextValue = useMemo(() => ({
    comments: commentsWithUsers,
    error: commentErrorMessage || userErrorMessage,
    loading: commentsQueryResult?.loading || usersLoading
  }), [
    commentsWithUsers,
    commentErrorMessage,
    userErrorMessage,
    commentsQueryResult,
    usersLoading
  ])

  if (!isAuthEnabled) {
    return (
      <CommentDataContext.Provider
        value={valueWhenAuthEnabledFalse}
      >
        {children}
      </CommentDataContext.Provider>
    )
  }

  return (
    <CommentDataContext.Provider
      value={commentDataContextValue}
    >
      {children}
    </CommentDataContext.Provider>
  )
}

export const PipelineCommentsProvider = ({ pipeline, children }) => {
  const [canLoadMoreComments, setCanLoadMoreComments] = useState(true);

  const { isAuthEnabled } = useClientContext();
  const { userData } = useUserContext();
  const { currentWorkspaceId } = useWorkspaceContext();

  const COMMENT_LIMIT = 50;
  // We need to execute this query lazily because we need to wait for the model query to load
  // and give us it's primary ID.
  const [queryComments, commentsQueryResult] = useLazyQuery(FETCH_PIPELINES_COMMENTS_QUERY);
  const pipelineID = pipeline?.id;
  useEffect(() => {
    if (!isAuthEnabled || !pipelineID) {
      return;
    }

    queryComments({ variables: { pipeline_pk_id: pipelineID, limit: COMMENT_LIMIT, idFilter: [] } });
  }, [pipelineID, queryComments, isAuthEnabled]);

  const comments
    = commentsQueryResult.called
      && !commentsQueryResult.loading
      && !commentsQueryResult.error
      && Object.keys(commentsQueryResult.data).includes('pipeline_comment')
      ? commentsQueryResult.data.pipeline_comment.map(({ comment, pipeline_pk_id, pipeline }) => ({
        ...comment,
        pipeline_id: pipeline_pk_id,
        workspace_id: pipeline?.workspace_id,
      })) : null;

  const filteredComments = comments?.filter(({ workspace_id }) => workspace_id === currentWorkspaceId) || null;

  const handleLoadMoreComments = useCallback(() => {
    if (!filteredComments || !commentsQueryResult.fetchMore) {
      queryComments({ variables: { pipeline_pk_id: pipelineID, limit: COMMENT_LIMIT, idFilter: [] } });
      return;
    }

    // The autoincrementing comment ID is used as the cursor for fetching more rows.
    const minCommentID = Math.min(...filteredComments.map(({ id }) => id));
    return commentsQueryResult
      .fetchMore({
        variables: {
          pipeline_pk_id: pipelineID,
          limit: COMMENT_LIMIT,
          idFilter: [{ comment_pk_id: { _lt: minCommentID } }],
        },
      })
      .then((response) => {
        const numComments = response.data?.pipeline_comment?.length;
        if (numComments === undefined) {
          return;
        }
        if (numComments < COMMENT_LIMIT) {
          setCanLoadMoreComments(false);
        }
      });
  }, [commentsQueryResult, filteredComments, pipelineID, queryComments]);

  const { data: commentsWithUsers, error: userError, loading: usersLoading } = useCommentsWithUsers(filteredComments);

  const [addPipelineComment] = useMutation(ADD_PIPELINE_COMMENT_QUERY, {
    update: (cache, { data: { insert_pipeline_comment_one } }) => {
      cache.writeQuery({
        query: FETCH_PIPELINES_COMMENTS_QUERY,
        variables: { pipeline_pk_id: pipelineID },
        data: { pipeline_comment: [insert_pipeline_comment_one] },
      });
    },
  });

  const currentUserId = userData?.id;
  const handleAddComment = useCallback((message) => {
    if (!currentUserId || !pipelineID || !message) {
      return;
    }

    const tempCommentID = Math.random().toString();
    addPipelineComment({
      variables: { pipeline_pk_id: pipelineID, message, user_id: currentUserId },
      optimisticResponse: {
        insert_pipeline_comment_one: {
          id: `temp-pipeline-variant-comment-id__${tempCommentID}`,
          pipeline_pk_id: pipelineID,
          __typename: 'pipeline_comment',
          comment: {
            id: `temp-comment-id__${tempCommentID}`,
            message,
            user_id: currentUserId,
            created_at: formatISO(new Date()),
            updated_at: formatISO(new Date()),
            deleted_at: null,
            __typename: 'comment',
          },
        },
      },
    });
  }, [addPipelineComment, currentUserId, pipelineID])

  const [updatePipelineComment] = useMutation(UPDATE_PIPELINE_COMMENT_QUERY, {});
  const handleUpdateComment = useCallback((commentID, updatedMessage) => {
    updatePipelineComment({ variables: { commentID, updatedMessage } });
  }, [updatePipelineComment]);

  const [deletePipelineComment] = useMutation(DELETE_PIPELINE_COMMENT_QUERY, {});
  const handleDeleteComment = useCallback((commentID) => {
    deletePipelineComment({ variables: { commentID, deletedAt: new Date().toISOString() } });
  }, [deletePipelineComment]);

  // There's no great way to get a "has next page" variable back from GraphQL, so just once
  // after the initial render we'll check if we have fewer results than we asked for and, if
  // so, hide the "load more comments" button.
  const [hasInitialCommentLoadCompleted, setHasInitialCommentLoadCompleted] = useState(false);
  useEffect(() => {
    if (hasInitialCommentLoadCompleted || filteredComments === null) {
      return;
    }
    setHasInitialCommentLoadCompleted(true);

    if (filteredComments.length < COMMENT_LIMIT) {
      setCanLoadMoreComments(false);
    }
  }, [filteredComments, hasInitialCommentLoadCompleted]);

  const userErrorMessage = userError ? 'Could not load user data for comments' : null;
  const commentErrorMessage = commentsQueryResult?.error ? 'Could not retrieve pipeline comments.' : null

  const valueWhenAuthEnabledFalse = {
    error: 'Log in to see pipeline comments.',
    loading: false,
    canLoadMoreComments: false,
    comments: [],
    addComment: () => { },
    updateComment: () => { },
    deleteComment: () => { },
    loadMoreComments: () => { },
  }

  const commentDataContextValue = useMemo(() => ({
    comments: commentsWithUsers,
    error: commentErrorMessage || userErrorMessage,
    loading: commentsQueryResult?.loading || usersLoading,
    canLoadMoreComments,
    addComment: handleAddComment,
    updateComment: handleUpdateComment,
    deleteComment: handleDeleteComment,
    loadMoreComments: handleLoadMoreComments,
  }), [
    commentsWithUsers,
    commentErrorMessage,
    userErrorMessage,
    commentsQueryResult,
    usersLoading,
    canLoadMoreComments,
    handleAddComment,
    handleUpdateComment,
    handleDeleteComment,
    handleLoadMoreComments,
  ])

  if (!isAuthEnabled) {
    return (
      <CommentDataContext.Provider
        value={valueWhenAuthEnabledFalse}
      >
        {children}
      </CommentDataContext.Provider>
    )
  }

  return (
    <CommentDataContext.Provider
      value={commentDataContextValue}
    >
      {children}
    </CommentDataContext.Provider>
  )
}

export const useCommentContext = () => useContext(CommentDataContext);

export default useCommentContext;
